import React from "react";
import dayjs from "dayjs";
import {
  Badge, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { actionLabel, SENSITIVE_ACTIONS } from "./auditLogConstants";
import { BODY_CELL, HEAD_CELL } from "./auditLogStyles";

const HEADERS = ["Date", "Admin", "Action", "Cible", "Motif", "IP"];

/**
 * Builds a short label for the audited target (resource or impacted user).
 * @param {Object} row - The audit log row
 * @returns {string} A target label
 */
function targetLabel(row) {
  if (row.target_resource) {
    return row.target_resource_id ? `${row.target_resource} #${row.target_resource_id}` : row.target_resource;
  }
  return row.target_user_id ? `Utilisateur #${row.target_user_id}` : "—";
}

/**
 * Renders a single audit log row; clicking it opens the detail drawer.
 * @param {Object} props - row, onSelect
 * @returns {React.ReactElement}
 */
function AuditRow({ row, onSelect }) {
  const color = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const sensitive = SENSITIVE_ACTIONS.has(row.action);
  return (
    <Tr cursor="pointer" _hover={{ bg: hoverBg }} onClick={() => onSelect(row)}>
      <Td {...BODY_CELL} color={color}>{dayjs(row.created_at).format("DD/MM/YYYY HH:mm:ss")}</Td>
      <Td {...BODY_CELL} color={color}>{row.admin_email || `#${row.admin_id}` || "—"}</Td>
      <Td {...BODY_CELL}>
        <Badge colorScheme={sensitive ? "red" : "gray"} variant="subtle" borderRadius="6px">{actionLabel(row.action)}</Badge>
      </Td>
      <Td {...BODY_CELL} color={color}>{targetLabel(row)}</Td>
      <Td {...BODY_CELL} color={color} maxW="240px" whiteSpace="normal">{row.reason || "—"}</Td>
      <Td {...BODY_CELL} color={color}>{row.ip_address || "—"}</Td>
    </Tr>
  );
}

/**
 * Renders the audit log table, or an empty state when there are no entries.
 * @param {Object} props - items, onSelect
 * @returns {React.ReactElement}
 */
function AuditLogTable({ items, onSelect }) {
  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  if (!items.length) {
    return (
      <Card>
        <Text textAlign="center" py="20px" fontSize="sm" color={emptyColor}>
          Aucune entrée d'audit pour ces filtres.
        </Text>
      </Card>
    );
  }
  return (
    <Card px="0" py="0" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>{HEADERS.map((h) => <Th key={h} {...HEAD_CELL} color={headColor}>{h}</Th>)}</Tr>
          </Thead>
          <Tbody>
            {items.map((row) => <AuditRow key={row.id} row={row} onSelect={onSelect} />)}
          </Tbody>
        </Table>
      </TableContainer>
    </Card>
  );
}

export default AuditLogTable;
